import { useState } from 'react'

/**
 * Numeric PIN keypad. Large touch targets (NFR-03), no free-text keyboard so
 * the PIN is never offered to autofill or spellcheck. The entered digits are
 * kept only in component state and handed to `onSubmit`; they are never
 * logged or persisted.
 */

export interface PinPadProps {
  readonly title: string
  readonly hint?: string
  readonly error?: string | null
  readonly minLength: number
  readonly maxLength: number
  readonly busy?: boolean
  readonly disabled?: boolean
  readonly submitLabel?: string
  readonly onSubmit: (pin: string) => void
  readonly onCancel?: () => void
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const

export function PinPad({
  title,
  hint,
  error,
  minLength,
  maxLength,
  busy = false,
  disabled = false,
  submitLabel = 'Xác nhận',
  onSubmit,
  onCancel,
}: PinPadProps): React.JSX.Element {
  const [pin, setPin] = useState('')
  const locked = busy || disabled

  const press = (digit: string) => {
    if (!locked) setPin((p) => (p.length < maxLength ? p + digit : p))
  }
  const submit = () => {
    if (locked || pin.length < minLength) return
    onSubmit(pin)
    setPin('')
  }

  const keyClass =
    'h-16 rounded-2xl bg-slate-100 text-2xl font-semibold text-slate-900 active:bg-slate-200 disabled:opacity-40 dark:bg-slate-800 dark:text-slate-100 dark:active:bg-slate-700'

  return (
    <div className="mx-auto flex w-full max-w-xs flex-col items-center gap-5">
      <h2 className="text-center text-xl font-semibold">{title}</h2>
      {hint !== undefined && (
        <p className="text-center text-slate-600 dark:text-slate-400">{hint}</p>
      )}

      <div
        className="flex gap-3"
        role="status"
        aria-label={`Đã nhập ${String(pin.length)} chữ số`}
      >
        {Array.from({ length: maxLength }, (_, i) => (
          <span
            key={i}
            className={`size-4 rounded-full border-2 border-slate-400 ${
              i < pin.length ? 'bg-slate-800 dark:bg-slate-100' : ''
            } ${i >= minLength && i >= pin.length ? 'opacity-40' : ''}`}
          />
        ))}
      </div>

      <p className="min-h-6 text-center text-rose-700 dark:text-rose-400" role="alert">
        {error ?? ''}
      </p>

      <div className="grid w-full grid-cols-3 gap-3">
        {KEYS.map((d) => (
          <button
            key={d}
            type="button"
            className={keyClass}
            disabled={locked}
            onClick={() => {
              press(d)
            }}
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          className={keyClass}
          disabled={locked || pin.length === 0}
          aria-label="Xóa chữ số"
          onClick={() => {
            setPin((p) => p.slice(0, -1))
          }}
        >
          ⌫
        </button>
        <button
          type="button"
          className={keyClass}
          disabled={locked}
          onClick={() => {
            press('0')
          }}
        >
          0
        </button>
        <button
          type="button"
          className="h-16 rounded-2xl bg-sky-700 text-lg font-semibold text-white active:bg-sky-800 disabled:opacity-40"
          disabled={locked || pin.length < minLength}
          onClick={submit}
        >
          {busy ? '…' : submitLabel}
        </button>
      </div>

      {onCancel !== undefined && (
        <button
          type="button"
          className="px-4 text-slate-600 underline dark:text-slate-400"
          onClick={onCancel}
        >
          Hủy
        </button>
      )}
    </div>
  )
}
